import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { ConfirmationModalType } from "../components/ConfirmationModal";

type DataStoreValues = { confirmationModal: ConfirmationModalType };
type StorePaths = {
  confirmationModal: ConfirmationModalType;
  "confirmationModal.open": boolean;
};
type DataStoreContextValue = {
  data: DataStoreValues;
  setStoreData: <P extends keyof StorePaths>(
    path: P,
    value: StorePaths[P],
  ) => void;
  cleanStoreData: () => void;
};

export const emptyStoreState: DataStoreValues = {
  confirmationModal: { open: false, actions: [] },
};
const DataStoreContext = createContext<DataStoreContextValue>({
  data: emptyStoreState,
  setStoreData: () => {},
  cleanStoreData: () => {},
});

export function DataStoreProvider({
  children,
  initialData,
}: React.PropsWithChildren<{ initialData: DataStoreValues }>) {
  const [data, setData] = useState(initialData);
  const setStoreData = useCallback<DataStoreContextValue["setStoreData"]>(
    (path, value) => {
      setData((current) => ({
        confirmationModal:
          path === "confirmationModal"
            ? (value as ConfirmationModalType)
            : { ...current.confirmationModal, open: value as boolean },
      }));
    },
    [],
  );
  const cleanStoreData = useCallback(() => setData(emptyStoreState), []);
  const value = useMemo(
    () => ({ data, setStoreData, cleanStoreData }),
    [data, setStoreData, cleanStoreData],
  );
  return (
    <DataStoreContext.Provider value={value}>
      {children}
    </DataStoreContext.Provider>
  );
}

export const useDataStore = () => useContext(DataStoreContext);

export function useStore<P extends keyof StorePaths>(
  path: P,
): [StorePaths[P], (value: StorePaths[P]) => void] {
  const { data, setStoreData } = useDataStore();
  const value = (
    path === "confirmationModal"
      ? data.confirmationModal
      : data.confirmationModal.open
  ) as StorePaths[P];
  const setValue = useCallback(
    (nextValue: StorePaths[P]) => setStoreData(path, nextValue),
    [path, setStoreData],
  );
  return [value, setValue];
}
